import { ComponentProps, useMemo, useState } from 'react';
import { Modal as RSModal } from 'reactstrap';
import { OfficeSelector } from 'src/components/Selectors/OfficeSelector';
import { Content } from 'src/contexts/ContentContext';
import { GenericDialog } from 'src/core/interim/dialogs/GenericDialog';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Button, Stack } from 'src/core/ui';
import { useOffices } from 'src/hooks/api/useOffices';
import { ContentId } from 'src/utils/constants/contentId';
import { Office } from 'src/WebApiController';

export type ChangeOfficeDialogProps = ComponentProps<typeof RSModal> & {
  currentOfficeId?: number | null;
  isLoading?: boolean;
  onOkay: (office: Office) => void;
  onCancel: () => void;
};

export function ChangeOfficeDialog({
  currentOfficeId,
  isLoading,
  onOkay,
  onCancel,
  size,
  ...rest
}: ChangeOfficeDialogProps) {
  const { query: officesQuery } = useOffices();
  const offices = officesQuery.data ?? [];

  const [selectedOfficeId, setSelectedOfficeId] = useState<string | undefined>(
    undefined
  );

  const selectedOffice = useMemo(
    () =>
      selectedOfficeId != null
        ? offices.find((o) => o.officeId === Number(selectedOfficeId))
        : undefined,
    [offices, selectedOfficeId]
  );

  return (
    <GenericDialog
      {...rest}
      size="m"
      fullscreen={size === 'fullscreen'}
      header={<Content id={ContentId.ChangeOffice} />}
      onOpened={() => {
        setSelectedOfficeId(undefined);
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      footer={
        <>
          <Button
            variant="outline"
            disabled={isLoading}
            onClick={() => {
              onCancel();
            }}
          >
            <Content id={ContentId.Cancel} />
          </Button>

          <Button
            disabled={isLoading || selectedOffice == null}
            onClick={() => {
              if (selectedOffice) {
                onOkay(selectedOffice);
              }
            }}
          >
            <Content id={ContentId.Save} />
          </Button>
        </>
      }
      onCancel={isLoading ? undefined : onCancel}
    >
      <Stack direction="column" gap="m">
        <Stack direction="column" gap="s">
          <Content id={ContentId.SelectOffice} />
          {isLoading ? (
            <PosSpinner />
          ) : (
            <OfficeSelector
              value={selectedOfficeId}
              currentOfficeId={currentOfficeId}
              onChange={(value) => setSelectedOfficeId(value)}
              disabled={isLoading}
            />
          )}
        </Stack>

        <Content id={ContentId.ChangeOfficeWarningListingRecreated} />
      </Stack>
    </GenericDialog>
  );
}
